import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Copy, FileText, Info, Loader2, Phone, Plus, Sparkles, X } from "lucide-react";
import { API } from "@/constants";

const PlatformApplicationsPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [filter, setFilter] = useState('all');
  const [codeForm, setCodeForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    case_type: 'personal_injury',
    notes: '',
    expires_days: 7
  });
  const [generatedCode, setGeneratedCode] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  useEffect(() => {
    loadCases();
  }, [filter]);
  
  const loadCases = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? `${API}/intake/cases` : `${API}/intake/cases?status=${filter}`;
      const res = await axios.get(url);
      setCases(res.data.cases || []);
    } catch (e) {
      console.error('Failed to load cases:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const createIntakeCode = async () => {
    try {
      const res = await axios.post(`${API}/intake/create-code`, codeForm);
      setGeneratedCode(res.data);
      loadCases();
    } catch (e) {
      alert('Failed to create access code');
    }
  };
  
  const reviewCase = async (caseId, action, notes = '') => {
    try {
      await axios.post(`${API}/intake/cases/${caseId}/review`, { action, notes });
      loadCases();
      if (selectedCase?.submission?.case_id === caseId) {
        const res = await axios.get(`${API}/intake/cases/${caseId}`);
        setSelectedCase(res.data);
      }
    } catch (e) {
      alert('Failed to update case');
    }
  };
  
  const analyzeCase = async (caseId) => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/intake/cases/${caseId}/analyze`);
      // Reload case to get analysis
      const caseRes = await axios.get(`${API}/intake/cases/${caseId}`);
      setSelectedCase(caseRes.data);
      loadCases();
    } catch (e) {
      alert('Analysis failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAnalyzing(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#FEF3C7', color: '#D97706' },
      submitted: { bg: '#DBEAFE', color: '#2563EB' },
      reviewing: { bg: '#E0E7FF', color: '#4F46E5' },
      approved: { bg: '#D1FAE5', color: '#059669' },
      rejected: { bg: '#FEE2E2', color: '#DC2626' },
      assigned: { bg: '#F3E8FF', color: '#9333EA' },
      info_requested: { bg: '#FED7AA', color: '#EA580C' }
    };
    const style = styles[status] || styles.pending;
    return <Badge style={{ background: style.bg, color: style.color }}>{status?.replace('_', ' ')?.toUpperCase()}</Badge>;
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <PlatformLayout activeTab="applications">
      <div className="rounded-3xl p-6 mb-6" style={{ background: '#fff', border: '1px solid #D9E4F2', boxShadow: '0 12px 30px rgba(15,23,42,.08)' }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-extrabold uppercase tracking-wider mb-2" style={{ color: '#57759C' }}>Client Intake System</div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#1E293B' }}>Case Applications & Review</h1>
            <p style={{ color: '#475569' }}>Generate access codes for clients to submit cases. Review, analyze, and manage incoming case applications.</p>
          </div>
          <Button onClick={() => setShowCreateCode(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Create Access Code
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {['all', 'pending', 'submitted', 'reviewing', 'approved', 'rejected'].map(f => (
          <Button 
            key={f} 
            variant={filter === f ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'bg-blue-600' : ''}
          >
            {f === 'all' ? 'All Cases' : f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>
      
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Cases List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Case Applications</CardTitle>
              <CardDescription>{cases.length} cases found</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                </div>
              ) : cases.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-muted-foreground">No cases found</p>
                  <Button onClick={() => setShowCreateCode(true)} className="mt-4">
                    Create First Access Code
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cases.map((c, i) => (
                    <div 
                      key={i}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${selectedCase?.access_code === c.access_code ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setSelectedCase(c)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{c.submission?.client_name || c.client_name}</h4>
                            {getStatusBadge(c.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {c.submission?.case_type || c.case_type} • Code: <code className="bg-slate-100 px-1 rounded">{c.access_code}</code>
                          </p>
                          {c.submission?.case_id && (
                            <p className="text-xs text-muted-foreground mt-1">Case ID: {c.submission.case_id}</p>
                          )}
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          {new Date(c.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      {c.submission?.case_summary && (
                        <p className="text-sm mt-2 line-clamp-2" style={{ color: '#475569' }}>
                          {c.submission.case_summary}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Case Detail / Actions */}
        <div>
          {selectedCase ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Case Details</span>
                  {getStatusBadge(selectedCase.status)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Client</Label>
                  <p className="font-semibold">{selectedCase.submission?.client_name || selectedCase.client_name}</p>
                  <p className="text-sm">{selectedCase.submission?.client_email || selectedCase.client_email}</p>
                  {(selectedCase.submission?.client_phone || selectedCase.client_phone) && (
                    <p className="text-sm">{selectedCase.submission?.client_phone || selectedCase.client_phone}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Access Code</Label>
                  <div className="flex items-center gap-2">
                    <code className="bg-slate-100 px-2 py-1 rounded font-bold">{selectedCase.access_code}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(`${window.location.origin}/intake/${selectedCase.access_code}`)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {selectedCase.submission && (
                  <>
                    <div>
                      <Label className="text-xs text-muted-foreground">Case Type</Label>
                      <p className="capitalize">{selectedCase.submission.case_type?.replace('_', ' ')}</p>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-muted-foreground">Case Summary</Label>
                      <p className="text-sm">{selectedCase.submission.case_summary}</p>
                    </div>
                    
                    {selectedCase.submission.opposing_party && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Opposing Party</Label>
                        <p className="text-sm">{selectedCase.submission.opposing_party}</p>
                      </div>
                    )}
                    
                    {selectedCase.submission.damages_estimate && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Damages Estimate</Label>
                        <p className="text-sm font-semibold text-emerald-600">{selectedCase.submission.damages_estimate}</p>
                      </div>
                    )}
                    
                    {selectedCase.documents?.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Documents ({selectedCase.documents.length})</Label>
                        <div className="space-y-1 mt-1">
                          {selectedCase.documents.map((doc, i) => (
                            <div key={i} className="text-xs flex items-center gap-2 p-2 bg-slate-50 rounded">
                              <FileText className="h-4 w-4" />
                              {doc.file_name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* AI Analysis */}
                {selectedCase.ai_analysis && (
                  <div>
                    <Label className="text-xs text-muted-foreground">AI Analysis</Label>
                    <div className="text-sm p-3 bg-purple-50 rounded-lg border border-purple-200 mt-1 whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {selectedCase.ai_analysis}
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <Separator />
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Actions</Label>
                  
                  {selectedCase.submission && !selectedCase.ai_analysis && (
                    <Button 
                      onClick={() => analyzeCase(selectedCase.submission.case_id)} 
                      disabled={analyzing}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Run AI Analysis
                    </Button>
                  )}
                  
                  {selectedCase.submission && selectedCase.status !== 'approved' && (
                    <Button 
                      onClick={() => reviewCase(selectedCase.submission.case_id, 'approve')} 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Approve Case
                    </Button>
                  )}
                  
                  {selectedCase.submission && selectedCase.status !== 'rejected' && (
                    <Button 
                      onClick={() => reviewCase(selectedCase.submission.case_id, 'reject')} 
                      variant="outline"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <X className="h-4 w-4 mr-2" /> Reject Case
                    </Button>
                  )}
                  
                  {selectedCase.submission && (
                    <Button 
                      onClick={() => reviewCase(selectedCase.submission.case_id, 'request_info')} 
                      variant="outline"
                      className="w-full"
                    >
                      <Info className="h-4 w-4 mr-2" /> Request More Info
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-muted-foreground">Select a case to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Create Code Modal */}
      {showCreateCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => { setShowCreateCode(false); setGeneratedCode(null); }}>
          <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{generatedCode ? 'Access Code Created' : 'Create Client Access Code'}</CardTitle>
              <CardDescription>
                {generatedCode ? 'Share this code with your client' : 'Generate a code for a client to submit their case'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {generatedCode ? (
                <div className="text-center space-y-4">
                  <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200">
                    <p className="text-sm text-emerald-700 mb-2">Access Code</p>
                    <p className="text-4xl font-bold tracking-wider text-emerald-700">{generatedCode.access_code}</p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Client Intake Link</p>
                    <code className="text-xs break-all">{window.location.origin}/intake/{generatedCode.access_code}</code>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={() => copyToClipboard(generatedCode.access_code)} variant="outline" className="flex-1">
                      <Copy className="h-4 w-4 mr-2" /> Copy Code
                    </Button>
                    <Button onClick={() => copyToClipboard(`${window.location.origin}/intake/${generatedCode.access_code}`)} className="flex-1 bg-blue-600">
                      <Copy className="h-4 w-4 mr-2" /> Copy Link
                    </Button>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Expires: {new Date(generatedCode.expires_at).toLocaleDateString()}
                  </p>
                  
                  <Button onClick={() => { setShowCreateCode(false); setGeneratedCode(null); }} variant="outline" className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Client Name *</Label>
                    <Input 
                      value={codeForm.client_name}
                      onChange={e => setCodeForm(f => ({ ...f, client_name: e.target.value }))}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label>Client Email *</Label>
                    <Input 
                      type="email"
                      value={codeForm.client_email}
                      onChange={e => setCodeForm(f => ({ ...f, client_email: e.target.value }))}
                      placeholder="client@email.com"
                    />
                  </div>
                  <div>
                    <Label>Client Phone</Label>
                    <Input 
                      value={codeForm.client_phone}
                      onChange={e => setCodeForm(f => ({ ...f, client_phone: e.target.value }))}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div>
                    <Label>Case Type</Label>
                    <Select value={codeForm.case_type} onValueChange={v => setCodeForm(f => ({ ...f, case_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal_injury">Personal Injury</SelectItem>
                        <SelectItem value="insurance_bad_faith">Insurance Bad Faith</SelectItem>
                        <SelectItem value="employment">Employment</SelectItem>
                        <SelectItem value="contract">Contract Dispute</SelectItem>
                        <SelectItem value="fraud">Fraud</SelectItem>
                        <SelectItem value="medical_malpractice">Medical Malpractice</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea 
                      value={codeForm.notes}
                      onChange={e => setCodeForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Notes about this client or case..."
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setShowCreateCode(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button 
                      onClick={createIntakeCode} 
                      disabled={!codeForm.client_name || !codeForm.client_email}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      Generate Code
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </PlatformLayout>
  );
};

// PlatformBillingPage, PlatformAdminPage, PlatformDocumentsPage are now imported from @/pages/platform


export default PlatformApplicationsPage;
